<template>
	<div class="store-selector">
		<div v-show="searchMode" class="store-selector-list" id="search-section">
			<location-search @locationChanged="locationChanged" :restrictToSweden="false" />
			<ul class="list-group list-store-group">
				<store-selector-stock-row
					v-for="store in (sortedStores || topStores)"
					:key="store.id"
					:selected="selectedStore === store.id"
					:stock="computedStockMap"
					:storeId="store.id"
					:cartRows="cartRows"
					:storeDistance="sortedStores && store.dist !== undefined ? '(' + formatDistance(store.dist) + ')' : ''"
					@click.native="click(store)"
				/>
			</ul>
			<a class="search-mode" @click="searchMode = false">{{ tc('SHOW_ALL_STORES') }}</a>
		</div>
		<div v-if="! searchMode" class="store-selector-list">
			<div v-for="(group, index) in mappedStoreGroups" class="store-group" :key="'store-group-format-' + index">
				<h3 class="store-group-name" >{{ t(group.name) }}</h3>
				<template v-if="group.subGroups">
					<template v-for="(subGroup, index) in group.subGroups">
						<h4 class="store-group-name" :key="'sub-group-' + index">{{ subGroup.location }}</h4>
						<ul class="list-group list-store-group" :key="'sub-group-list-' + index">
							<store-selector-stock-row
								v-for="(store, index) in subGroup.stores"
								:key="'group-store-' + index"
								:selected="selectedStore === store"
								:stock="computedStockMap"
								:storeId="store"
								:cartRows="cartRows"
								@click.native="selectedStore = store"
							/>
						</ul>
					</template>
				</template>
				<ul v-else class="list-group list-store-group">
					<store-selector-stock-row
						v-for="(store, index) in group.stores"
						:key="'group-store-' + index"
						:selected="selectedStore === store"
						:stock="computedStockMap"
						:storeId="store"
						:cartRows="cartRows"
						@click.native="selectedStore = store"
					/>
				</ul>
			</div>
			<a class="search-mode" href="#search-section" @click="searchMode = true">{{ tc('SEARCH_FOR_STORE') }}</a>
		</div>
		<store-info-container
			v-if="selectedStoreInfo && selectedStore"
			key="storeinfo"
			:store="selectedStoreInfo"
			smallMap
			no-images
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { t, storeName, getStore } from 'datastore/i18n';
import { Autocomplete } from 'vue2-google-maps';
import { cloneDeep } from 'lodash';
import { formatDistance, geoDistSort } from 'utils';

const NUM_STORES = 6;

export default {
	data () {
		return {
			formatDistance,
			geoDistSort,
			selectedStore: this.value,
			selectedStoreInfo: null,
			searchMode: true,
			location: null,
		};
	},
	components: {
		gAutocomplete: Autocomplete,
	},
	props: {
		stockMap: { required: true },
		value: { required: true },
		cartRows: { required: true },
	},
	mounted () {
		if (this.selectedStore) {
			this.selectedStoreInfo = getStore(this.selectedStore);
		}
		for (const store of this.topStores) {
			if (this.computedStockMap[store.id]) {
				this.selectedStore = store.id;
				break;
			}
			if (this.userSettings.myStores.length > 0 && this.userSettings.myStores.indexOf(this.selectedStore) === -1) {
				this.selectedStore = this.userSettings.myStores[0];
				break;
			}
		}
	},
	computed: {
		computedStockMap () { // @TODO duplicerad i shipping-method-selector
			const stock = {};
			for (const row of this.cartRows) {
				const qty = row.quantity;
				const prodId = row.product.id + '';
				const stockMap = this.stockMap[prodId];
				for (const store in stockMap) {
					const storeId = store + '';
					const inStock = stockMap[storeId] >= qty;
					if (stock[storeId] === undefined) {
						stock[storeId] = inStock ? stockMap[storeId] : false;
					} else if (stock[storeId] && ! inStock) {
						stock[storeId] = false;
					} else if (stockMap[storeId] < stock[storeId]) {
						// Always store the least amount of products available.
						stock[storeId] = stockMap[storeId];
					}
				}
			}
			return stock;
		},
		topStores () {
			const stores = this.userSettings.myStores.slice();
			const suggestedStores = this.user.storeSuggestions;

			let fillStores = NUM_STORES - stores.length;

			for (let i = 0; i < suggestedStores.length && fillStores > 0; i++) {
				const store = suggestedStores[i];
				if (stores.includes(store)) continue;
				if (this.computedStockMap[store+'']) {
					stores.push(store);
					fillStores--;
				}
			}

			for (let i = 0; i < suggestedStores.length && fillStores > 0; i++) {
				const store = suggestedStores[i];
				if (stores.includes(store)) continue;

				stores.push(store);
				fillStores--;
			}
			return stores.map(storeId => getStore(storeId)).filter(store => !! store);
		},
		sortedStores () {
			const sortedStores = geoDistSort(this.location, this.stores);
			if (sortedStores) {
				return sortedStores.slice(0, NUM_STORES);
			}
			return null;
		},
		stores () {
			const stores = this.$store.state.i18n.stores;
			if (! stores) return null;
			const storeArr = [];
			for (const storeId in stores) {
				storeArr.push(cloneDeep(stores[storeId]));
			}
			return storeArr;
		},
		mappedStoreGroups () {
			const mappedStoreGroups = this.userSettings.storeGroupFormat.map((element) => {
				/* If first store in city array isn't a number it has subgroups */
				if (isNaN(element.stores[0])) {
					let allSubGroups = [];
					let subGroup = {
						'location': element.stores[0],
						'stores': [],
					};

					for (let i = 0; i < element.stores.length; i++) {
						if (isNaN(element.stores[i])) {
							if (subGroup.stores.length > 0) {
								allSubGroups.push(subGroup);
							}
							/* Reset variable for each group to prevent it overwriting itself */
							subGroup = {
								'location': '',
								'stores': [],
							};
							subGroup.location = element.stores[i];
						/* If at end of array, push all stores and subGroup one last time */
						} else if (i + 1 === element.stores.length) {
							subGroup.stores.push(element.stores[i]);
							allSubGroups.push(subGroup);
						} else {
							subGroup.stores.push(element.stores[i]);
						}
					}

					return {
						'name': element.name,
						'subGroups': allSubGroups
					}
				/* If a city doesnt have subgroups of store, just return as is */
				} else {
					return element;
				}
			});

			return mappedStoreGroups;
		},
		...mapState({
			userSettings: state => state.userSettings,
			user: state => state.user,
		}),
	},
	methods: {
		t,
		storeName,
		tc: t.prefix('CHECKOUT'),
		ts: t.prefix('STOCK'),
		locationChanged (location) {
			this.location = location;
		},
		click (store) {
			this.selectedStore = store.id;
			this.$emit('clickStore');
		},
	},
	watch: {
		selectedStore (store, fromStore) {
			if (! store) {
				return;
			}
			this.selectedStoreInfo = getStore(store);
			this.$emit('input', store);
		},
		value (to) {
			this.selectedStore = to;
		},
	},
};
</script>
